import { queryOptions, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * FiltrosAuditoria son los filtros del visor (#254), tal como viajan en la query del
 * backend (GET /api/v1/auditoria). Todos opcionales y combinados con AND; los vacíos no se
 * envían. `actor`, `id_registro` y `obra` son ids en texto (vienen de un <select>/<input>);
 * `desde`/`hasta` son YYYY-MM-DD (día completo, `hasta` inclusivo).
 */
export type FiltrosAuditoria = {
  actor?: string;
  accion?: string;
  tabla?: string;
  id_registro?: string;
  obra?: string;
  desde?: string;
  hasta?: string;
  ip?: string;
};

/** EventoAuditoria es una fila del visor: espejo de auditlog.EventoDTO. */
export type EventoAuditoria = {
  id: number;
  created_at: string;
  id_usuario: number | null;
  nombre_usuario: string | null;
  accion: string;
  tabla: string;
  id_registro: number | null;
  id_obra: number | null;
  codigo_obra: string | null;
  nombre_obra: string | null;
  ip: string | null;
  detalle: unknown;
};

/** PaginaAuditoria es la respuesta paginada por cursor keyset. */
export type PaginaAuditoria = {
  eventos: EventoAuditoria[];
  cursor: string;
  has_more: boolean;
};

/** ResumenAuditoria es la vigilancia de volumen + el catálogo de acciones y tablas. */
export type ResumenAuditoria = {
  filas_estimadas: number;
  bytes: number;
  primer_evento: string | null;
  ultimo_evento: string | null;
  acciones: string[];
  tablas: string[];
};

/**
 * AUDITORIA_QUERY_KEY es la raíz de las claves del visor. Es GLOBAL (no va namespaceada por
 * obra: el registro cruza todas) y queda deliberadamente FUERA de la allowlist de
 * persistencia offline (`shouldPersistQuery`): el rastro forense nunca toca el disco del
 * navegador.
 */
export const AUDITORIA_QUERY_KEY = ["auditoria"] as const;

/** auditoriaQueryKey compone la clave de una consulta con sus filtros. */
export function auditoriaQueryKey(filtros: FiltrosAuditoria) {
  return [...AUDITORIA_QUERY_KEY, filtros] as const;
}

/**
 * queryDeAuditoria construye la query string de los filtros, omitiendo los vacíos, y añade
 * el cursor de la página pedida. Es la MISMA forma que consume el export xlsx.
 */
export function queryDeAuditoria(filtros: FiltrosAuditoria, cursor?: string): string {
  const params = new URLSearchParams();
  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor != null && valor !== "") {
      params.set(clave, valor);
    }
  }
  if (cursor != null && cursor !== "") {
    params.set("cursor", cursor);
  }
  const query = params.toString();
  return query === "" ? "" : `?${query}`;
}

/** auditoriaResumenQueryOptions describe la consulta del resumen de volumen. */
export const auditoriaResumenQueryOptions = queryOptions({
  queryKey: [...AUDITORIA_QUERY_KEY, "resumen"] as const,
  queryFn: () => api.get<ResumenAuditoria>("/auditoria/resumen"),
});

/** useAuditoriaResumen expone el volumen del registro y el catálogo de acciones/tablas. */
export function useAuditoriaResumen() {
  return useQuery(auditoriaResumenQueryOptions);
}

/**
 * useAuditoria pagina el visor por cursor KEYSET: cada página devuelve el cursor de la
 * siguiente (vacío cuando no hay más), y «Cargar más» lo encadena. No se usa offset: el
 * registro solo crece y un offset profundo se degradaría y podría repetir o saltar filas.
 */
export function useAuditoria(filtros: FiltrosAuditoria) {
  return useInfiniteQuery({
    queryKey: auditoriaQueryKey(filtros),
    queryFn: ({ pageParam }) =>
      api.get<PaginaAuditoria>(`/auditoria${queryDeAuditoria(filtros, pageParam)}`),
    initialPageParam: "",
    getNextPageParam: (ultima: PaginaAuditoria) =>
      ultima.has_more && ultima.cursor !== "" ? ultima.cursor : undefined,
  });
}
